import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { Pager } from 'rentalui-finance';
import ResponsiveTable from './ResponsiveTable';
import { getAdminInvoiceStats } from '../api';

const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const PAGE_LIMIT = 25;

function SummaryCard({ label, value, sub, variant }) {
  return (
    <Col xs={12} sm={6} md={4}>
      <div className={`border rounded p-3 h-100 border-${variant || 'secondary'}-subtle`}>
        <div className="small text-muted">{label}</div>
        <div className="fs-4 fw-bold">{value}</div>
        {sub && <div className="small text-muted">{sub}</div>}
      </div>
    </Col>
  );
}

const SORT_OPTIONS = [
  { value: 'totalCount',      label: 'Total invoices' },
  { value: 'totalValue',      label: 'Total billed' },
  { value: 'lastGeneratedAt', label: 'Last generated' },
  { value: 'manualCount',     label: 'Manual' },
  { value: 'automaticCount',  label: 'Automatic' },
];

export default function InvoiceStatsPanel() {
  const [platform, setPlatform] = useState(null);
  const [owners, setOwners] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters / sort
  const [q, setQ] = useState('');
  const [draftQ, setDraftQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('totalCount');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const searchTimer = useRef(null);

  const load = useCallback((params) => {
    setLoading(true); setError('');
    getAdminInvoiceStats(params)
      .then((res) => {
        setPlatform(res.data?.platform || null);
        setOwners(res.data?.owners || []);
        setPagination(res.pagination || { page: 1, limit: PAGE_LIMIT, total: 0, hasMore: false });
      })
      .catch(() => setError('Unable to load invoice statistics.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = { page, limit: PAGE_LIMIT, sortBy, sortDir };
    if (q) params.q = q;
    if (typeFilter) params.type = typeFilter;
    load(params);
  }, [load, page, q, typeFilter, sortBy, sortDir]);

  const onSearchInput = (e) => {
    const val = e.target.value;
    setDraftQ(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setQ(val.trim());
      setPage(1);
    }, 350);
  };

  const onTypeChange = (val) => {
    setTypeFilter(val);
    setPage(1);
  };

  const onSortChange = (col) => {
    if (sortBy === col) {
      setSortDir((d) => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span className="text-muted ms-1 small">↕</span>;
    return <span className="ms-1 small">{sortDir === 'desc' ? '↓' : '↑'}</span>;
  };

  const thSort = (col, label) => (
    <th
      role="button" style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSortChange(col)}
      className={sortBy === col ? 'text-primary' : ''}
    >
      {label}<SortIcon col={col} />
    </th>
  );

  return (
    <div>
      <h6 className="mb-3">Invoice module usage</h6>

      {/* Platform summary — always unfiltered */}
      {platform && (
        <Row className="g-2 mb-4">
          <SummaryCard label="Total invoices generated" value={platform.totalInvoices} sub={`across ${platform.totalOwners} owner${platform.totalOwners === 1 ? '' : 's'}`} variant="primary" />
          <SummaryCard label="This month" value={platform.thisMonthCount} sub="invoices created this calendar month" variant="info" />
          <SummaryCard label="Total value billed" value={rupees(platform.totalValue)} sub="sum of all invoice amounts" variant="success" />
          <SummaryCard label="Manual" value={platform.manualCount} sub="owner-typed invoices" variant="secondary" />
          <SummaryCard label="Automatic" value={platform.automaticCount} sub="system-generated invoices" variant="secondary" />
          <SummaryCard label="Owners using invoicing" value={platform.totalOwners} variant="warning" />
        </Row>
      )}

      {/* Search + filters */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <InputGroup style={{ maxWidth: 280 }}>
          <Form.Control
            placeholder="Search by owner name or email"
            value={draftQ}
            onChange={onSearchInput}
            size="sm"
          />
          {draftQ && (
            <Button size="sm" variant="outline-secondary" onClick={() => { setDraftQ(''); setQ(''); setPage(1); }}>
              ✕
            </Button>
          )}
        </InputGroup>

        <ButtonGroup size="sm">
          {[['', 'All types'], ['MANUAL', 'Manual'], ['AUTOMATIC', 'Automatic']].map(([val, label]) => (
            <Button
              key={val}
              variant={typeFilter === val ? 'primary' : 'outline-secondary'}
              onClick={() => onTypeChange(val)}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>

        <Form.Select
          size="sm" style={{ maxWidth: 180 }}
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>Sort: {o.label}</option>
          ))}
        </Form.Select>

        <ButtonGroup size="sm">
          <Button variant={sortDir === 'desc' ? 'secondary' : 'outline-secondary'} onClick={() => { setSortDir('desc'); setPage(1); }}>↓ Desc</Button>
          <Button variant={sortDir === 'asc' ? 'secondary' : 'outline-secondary'} onClick={() => { setSortDir('asc'); setPage(1); }}>↑ Asc</Button>
        </ButtonGroup>

        {loading && <Spinner animation="border" size="sm" className="ms-1" />}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && owners.length === 0 && !error && (
        <Alert variant="light">
          {q || typeFilter ? 'No owners match your search.' : 'No invoices have been generated yet.'}
        </Alert>
      )}

      {owners.length > 0 && (
        <>
          <div className="small text-muted mb-2">
            {pagination.total} owner{pagination.total === 1 ? '' : 's'} · showing page {pagination.page}
            {q && <> · filtered by "<strong>{q}</strong>"</>}
            {typeFilter && <> · <Badge bg="info" text="dark">{typeFilter}</Badge> only</>}
          </div>

          <ResponsiveTable
            rows={owners}
            rowKey={(row) => row._id}
            empty="No results."
            columns={[
              {
                key: 'owner', header: 'Owner', card: 'title',
                cell: (row) => row.owner?.name || <span className="text-muted fst-italic">Unknown</span>
              },
              {
                key: 'email', header: 'Email', card: 'subtitle',
                cell: (row) => <span className="small text-muted">{row.owner?.email || '—'}</span>
              },
              {
                key: 'totalCount', header: <span role="button" onClick={() => onSortChange('totalCount')} style={{ cursor: 'pointer' }}>Total{sortBy === 'totalCount' && <SortIcon col="totalCount" />}</span>,
                className: 'text-end',
                cell: (row) => <span className="fw-semibold">{row.totalCount}</span>
              },
              {
                key: 'manualCount', header: <span role="button" onClick={() => onSortChange('manualCount')} style={{ cursor: 'pointer' }}>Manual{sortBy === 'manualCount' && <SortIcon col="manualCount" />}</span>,
                className: 'text-end',
                cell: (row) => row.manualCount > 0
                  ? <Badge bg="info" text="dark">{row.manualCount}</Badge>
                  : <span className="text-muted">—</span>
              },
              {
                key: 'automaticCount', header: <span role="button" onClick={() => onSortChange('automaticCount')} style={{ cursor: 'pointer' }}>Auto{sortBy === 'automaticCount' && <SortIcon col="automaticCount" />}</span>,
                className: 'text-end',
                cell: (row) => row.automaticCount > 0
                  ? <Badge bg="secondary">{row.automaticCount}</Badge>
                  : <span className="text-muted">—</span>
              },
              { key: 'activeCount', header: 'Active', className: 'text-end', cell: (row) => row.activeCount },
              {
                key: 'totalValue', header: <span role="button" onClick={() => onSortChange('totalValue')} style={{ cursor: 'pointer' }}>Total billed{sortBy === 'totalValue' && <SortIcon col="totalValue" />}</span>,
                className: 'text-end',
                cell: (row) => rupees(row.totalValue)
              },
              {
                key: 'lastGeneratedAt', header: <span role="button" onClick={() => onSortChange('lastGeneratedAt')} style={{ cursor: 'pointer' }}>Last generated{sortBy === 'lastGeneratedAt' && <SortIcon col="lastGeneratedAt" />}</span>,
                cell: (row) => <span className="small">{fmt(row.lastGeneratedAt)}</span>
              },
            ]}
          />

          <Pager
            page={pagination.page}
            total={pagination.total}
            totalPages={Math.ceil(pagination.total / pagination.limit)}
            limit={pagination.limit}
            hasMore={pagination.hasMore}
            loading={loading}
            setPage={setPage}
            setLimit={() => {}}
            showPageSize={false}
          />
        </>
      )}
    </div>
  );
}
